// 열람 화면의 껍데기 — **탭 둘. 전환이 아니라 보기 전환이다**
//
// 2026-08-15 결정으로 작전 타입이 배타적 상태에서 **나란히 선 보기 둘**이 됐다. attack 과
// defense 가 동시에 살아 있고 사람은 탭으로 오간다. 「attack 이 끝나야 defense 가 열린다」도
// 그 반대도 넣지 않는다 — 배타적으로 만들면 CT 가 수비를 짜려고 전환하는 순간 공격
// 카운트다운을 보던 roster 가 자기 차례를 잃고, **그것이 바로 이 제품이 없애려는 실패다.**
//
// ── 공격 열람을 언마운트하지 않는다 ─────────────────────────────────────────
//
// **이것이 이 파일에서 가장 중요한 한 줄이다.** 공격 열람의 신호 런타임은 마운트마다 만들어
// 지고 언마운트에서 `dispose()` 된다 — 「화면을 떠나면 걸어 둔 소리도 걷는다」가 그 스텝의
// 계약이고 그것은 옳다(다른 화면에서 이름 없는 카운트 소리가 울리면 안 된다). 그런데 탭은
// 화면을 떠나는 것이 **아니다.** 그래서 defense 탭에서도 공격 서브트리를 마운트한 채
// `hidden` 으로만 감춘다. 언마운트하면 **탭을 옮긴 순간 예약된 소리가 사라지고**, 그 사람은
// 자기 rally 를 소리 없이 놓친다.
//
// 대가가 둘이고 알고 치른다. 첫째, defense 탭일 때 상단 고정줄 노드가 DOM 에 둘 있다 —
// 감춰진 공격 쪽 하나와 이 껍데기가 그리는 defense 쪽 하나다. **보이는 것은 언제나 하나**
// 이지만 표식으로 찾는 검사는 그 사실을 알아야 한다. 둘째, 감춰진 공격 화면의 시계 동기가
// 계속 돌아 왕복이 두 벌이 된다. 둘 다 「소리가 걷히지 않는다」를 지키는 값이다.
//
// ── 신호 주인 한 줄 — 2026-08-16 개정 회차가 세웠다 ─────────────────────────
//
// 정본은 defense 탭 상단에 **그 소리가 누구 것인지와 개시까지 남은 시간**을 한 줄로 적으라고
// 한다. 처음에는 그 줄을 세우지 못했다 — 「무엇을 골랐는가」가 공격 열람 화면의 지역 상태이고
// 신호 런타임이 들고 있는 대상에는 이름이 없어서(`rallyId` 와 `openMs` 뿐), 그 디렉터리를 열지
// 않고는 값을 꺼낼 길이 없었기 때문이다.
//
// **그 자리를 비워 둔 대가가 곧바로 측정으로 나왔다.** 대리 관측이 오디오 예약 여섯이 살아
// 있는 것을 세어 놓고, 같은 화면 맨 아래가 「수비에는 소리 신호가 없습니다」라고 말하는 것을
// 함께 잡았다. 「카운트다운이 누구 것인지 항상 화면에 있다」는 부분 점수가 없는 항목이라 축이
// 통째로 0점이 됐다. 그래서 공격 열람 화면에 **내보내는 문 하나**를 더하는 쪽으로 갔고, 그
// 개정은 소유를 따라 공격 열람 스텝 이름의 커밋으로 먼저 착지한다.
//
// **선택 상태를 여기로 올리지 않았다.** 올리면 그 화면의 소리 예약·정렬·강조가 함께 딸려
// 올라와 되돌릴 단위가 두 스텝에 걸친다. 여기 필요한 것은 「지금 무엇이 걸려 있는가」 한
// 값뿐이다.

import { useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'

import { defense as defenseStrings, shell as shellStrings } from '../../../i18n/index.ts'
import { markSessionGone, readToken, useLiveState, VIEWER_TOKEN_KEY } from '../../access/client.ts'
import { enemyLines } from '../../defense/derive.ts'
import { defenseBandText } from '../../shell/band.ts'
import { signalLine, type SignalLine } from '../../defense/signal-line.ts'
import { useDefense } from '../../defense/use-defense.ts'
import { TopBar } from '../../shell/topbar.tsx'
import { Icon } from '../../ui/icons.tsx'
import { useShellClock } from '../../shell/use-shell-clock.ts'
import { readAttackRallies, subscribeAttackRallies } from '../../signal/live-attack.ts'
import { readSignalTarget, subscribeSignalTarget } from '../../signal/live-target.ts'
import { ViewAttackScreen } from '../view-attack/index.tsx'
import { ViewDefenseScreen } from '../view-defense/index.tsx'
import { PresenceList } from '../entry/index.tsx'
import { useLang } from '../../shell/use-lang.ts'

/** 0.1초 칸이 흐르려면 그보다 잦게 그려야 한다. 모듈 상수라 렌더마다 새로 만들지 않는다. */
const CLOCK = { tickMs: 100 } as const

/**
 * 신호 줄의 시제(개시 전 둘, 개시 뒤 넷)가 각각 어느 문구를 쓰는가.
 *
 * **삼항이 아니라 표인 이유는 누락을 컴파일이 잡게 하려는 것이다.** 시제 유니온에 값을
 * 더했는데 여기 짝을 안 넣으면 타입 검사가 곧바로 막는다. 삼항을 겹쳐 쓰면 새 값이
 * 조용히 마지막 갈래로 떨어져, 「껐다」가 「지나갔다」로 표시되는 종류의 거짓말이 검사도
 * 없이 화면에 선다.
 */
const SIGNAL_PHRASE: Record<
  SignalLine['tense'],
  (t: ReturnType<typeof defenseStrings>) => string
> = {
  upcoming: (t) => t.composeSignalOwner,
  passed: (t) => t.composeSignalPassed,
  muted: (t) => t.composeSignalMuted,
  passedMuted: (t) => t.composeSignalPassedMuted,
  passedRang: (t) => t.composeSignalPassedRang,
  passedMissed: (t) => t.composeSignalPassedMissed,
}

type ViewTab = 'attack' | 'defense'

export function ViewScreen(): React.ReactElement {
  const clock = useShellClock(CLOCK)
  // **이 껍데기와 공격 화면이 각자 고정줄을 그린다.** 탭 둘이 나란히 서고 공격 쪽은
  // 감춰진 채로 마운트돼 있어서 스위치가 화면에 둘 있는데, 언어를 각자 들면 **같은 화면의
  // 두 탭이 서로 다른 언어를 주장한다.** 2026-08-17 대리 관측이 그 상태를 관측했다 —
  // ATTACK 에서 EN 을 누르고 DEFENSE 로 옮기면 거기 스위치는 KO 였다.
  const { lang, choose: setLang } = useLang()
  const t = defenseStrings(lang)

  const [token] = useState<string | null>(() => readToken(VIEWER_TOKEN_KEY))
  const store = useDefense(token)

  // 입장하지 않은 사람에게는 탭을 보이지 않는다. 공격 열람 화면이 이미 「무엇이 필요한지와
  // 어디로 가면 되는지」를 말하고 있고, 그 안내를 두 벌로 적으면 둘이 서로 다르게 낡는다.
  const canSwitch = token !== null

  /**
   * 접속 명단. **손잡이를 펼칠 때가 아니라 이 껍데기가 설 때 붙는다** (T2 개정 둘째).
   *
   * 서버는 구독 직후와 명단이 바뀌는 사건 때만 명단을 방송한다. 펼칠 때 붙으면 그 뒤로 아무도
   * 드나들지 않는 한 빈 명단을 보고, 이 제품에서 아무도 드나들지 않는 조용한 시간은 전투
   * 직전이다. 소켓은 토큰당 하나를 나눠 쓰므로 붙는다고 연결이 늘지 않는다.
   */
  const live = useLiveState(token)
  const [presenceOpen, setPresenceOpen] = useState(false)

  // **서버가 이 세션을 모른다고 답하면 입장 화면으로 물러난다** (T4 개정 둘째). CT 가 명부에서
  // 지웠거나 추방했거나 방이 다시 만들어진 경우다. 판정 계약은 지운 사람의 화면이 한 왕복 안에
  // 코드 입력으로 돌아가기를 요구하는데, 이 화면은 그 신호를 받고도 읽지 않아 카운트다운과
  // 이름을 계속 흘렸다. 새 방송을 못 받는 낡은 화면을 그 사람이 믿는 것이 가장 나쁜 결과다.
  //
  // **기록을 쌓지 않고 주소를 바꾼다(replace).** 그래서 뒤로 가기가 죽은 화면으로 돌아오지 않는다.
  // 왜 돌아왔는지(지워졌는지, 추방됐는지)를 말하는 일은 입장 화면 한 곳이 진다.
  useEffect(() => {
    if (!live.sessionGone || token === null) return
    markSessionGone(token, live.goneCause ?? 'unknown')
    window.location.replace('/')
  }, [live.sessionGone, live.goneCause, token])
  // **한 번이라도 붙은 뒤에만 「끊김」을 적는다.** 처음 붙기 전의 `connected: false` 는 끊긴
  // 것이 아니라 아직 안 붙은 것이라, 그때 「연결 끊김」을 띄우면 화면을 열 때마다 거짓이 한 번
  // 지나간다. 같은 까닭으로 붙기 전에는 숫자 대신 말줄임을 둔다. 0명은 사실이 아니다.
  const [presenceSeen, setPresenceSeen] = useState(false)
  useEffect(() => {
    if (live.connected) setPresenceSeen(true)
  }, [live.connected])

  // **펼친 명단은 손잡이로만 접히지 않는다.** 전투 중에 실수로 펼친 사람은 바깥을 누르거나
  // Escape 로 닫으려 하는데, 그때 안 닫히면 명단이 카드를 덮은 채 남는다(2026-10-01 대리 관측).
  const dockRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!presenceOpen) return
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setPresenceOpen(false)
    }
    const onPointer = (event: PointerEvent): void => {
      const dock = dockRef.current
      if (dock !== null && event.target instanceof Node && !dock.contains(event.target)) {
        setPresenceOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
    }
  }, [presenceOpen])

  // **아래 여백을 고정 영역의 실제 높이로 잡는다.** 펼친 명단은 본문 위에 덮이므로, 여백이
  // 접힌 높이에 머물면 펼친 동안 마지막 카드와 끝 안내문에 영영 닿지 못한다(대리 관측, 340 폭).
  const [dockHeight, setDockHeight] = useState(0)
  useLayoutEffect(() => {
    const dock = dockRef.current
    if (dock === null) {
      document.documentElement.style.setProperty('--dock-h', '0px')
      return
    }
    const measure = (): void => {
      const height = dock.getBoundingClientRect().height
      setDockHeight(height)
      // attack 화면이 자기 `main` 맨 끝의 빈 칸 요소 높이로 쓴다(아래 빈 칸 주석).
      document.documentElement.style.setProperty('--dock-h', `${String(height + 16)}px`)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(dock)
    return () => {
      observer.disconnect()
      document.documentElement.style.setProperty('--dock-h', '0px')
    }
    // 이 화면은 0.1초마다 다시 그려진다. 매 렌더 관찰자를 새로 걸지 않도록, 고정 영역이 서고
    // 사라지는 때에만 다시 건다. 크기 변화는 관찰자가 알린다.
  }, [canSwitch])

  /**
   * **고르기 전에는 room 의 기본 탭을 따른다.** `null` 이 「아직 사람이 고르지 않았다」이고,
   * 그 상태에서만 서버 값이 화면을 정한다. 고른 뒤에는 서버 값이 바뀌어도 화면이 발밑에서
   * 움직이지 않는다 — CT 가 탭을 옮길 때마다 연맹원이 보던 화면이 갈아치워지면, 그 사람은
   * 자기가 무엇을 눌렀는지와 무관하게 화면을 잃는다.
   */
  const [picked, setPicked] = useState<ViewTab | null>(null)
  const active: ViewTab = picked ?? store.room?.operation ?? 'attack'

  /**
   * 지금 소리가 걸린 대상. 공격 열람 화면이 고를 때마다 알려 준다.
   *
   * **서버 스냅샷을 따로 두지 않는다** — 이 화면은 브라우저에서만 서고, 고른 것은 이
   * 브라우저의 메모리에만 있다. 세 번째 인자를 같은 함수로 두면 서버 렌더에서도 같은
   * `null` 을 읽어 어긋날 자리가 없다.
   */
  const liveTarget = useSyncExternalStore(subscribeSignalTarget, readSignalTarget, readSignalTarget)

  /** confirm 한 attack rally 수. 0 이면 attack 쪽에 고를 것이 없다. */
  const attackRallies = useSyncExternalStore(
    subscribeAttackRallies,
    readAttackRallies,
    readAttackRallies,
  )

  /**
   * 그 신호를 한 줄로 옮긴 것. **개시를 지났으면 시제와 부호가 함께 뒤집힌다.**
   *
   * 판단을 여기 인라인으로 두지 않는 이유는 이 저장소가 같은 결함에 세 번 걸렸기
   * 때문이다 — `T−` 뒤에 음수가 붙고 라벨이 「개시까지」로 남으면, 이미 지나간 순간을
   * 화면이 「앞으로 올 것」이라고 말한다.
   */
  const signal = signalLine(liveTarget, clock.utcMillis)

  /**
   * 상단 고정줄의 defense 아래층.
   *
   * **남은 시간은 다음 적 착탄까지 T− 다.** 수비에는 dispatch 가 없어 「내보내기 전에는
   * `T− --:--`」 규칙의 예외이고, 정본이 그 예외를 스스로 적었다. **요약은 적 rally 개수
   * 하나이고 착탄 미상을 합산한다** — 미상 행을 맨 아래 흐린 띠로 내린 대가를 여기서 갚는
   * 자리라, 합산에서 빼면 그 rally 가 화면에서 두 번 밀려난다.
   */
  // 글자까지 `src/web/shell/band.ts` 가 붙인다. 구성 화면의 DEFENSE 탭이 같은 함수를 부른다.
  const band = useMemo(
    () =>
      defenseBandText(
        enemyLines(store.enemyRallies, store.rosters, clock.utcMillis),
        clock.utcMillis,
        shellStrings(lang),
      ),
    [store.enemyRallies, store.rosters, clock.utcMillis, lang],
  )

  return (
    <>
      {/* **감추되 마운트는 유지한다.** `hidden` 은 그 서브트리의 고정줄까지 함께 감추므로,
          어느 순간에도 눈에 보이는 고정줄은 하나다. */}
      <div hidden={active === 'defense'} data-testid="view-attack-host">
        <ViewAttackScreen concealed={active === 'defense'} />
      </div>

      {active === 'defense' && (
        <>
          <TopBar
            utcMillis={clock.utcMillis}
            skew={clock.skew}
            lower={{ opType: 'defense', remaining: band.remaining, remainingState: band.remainingState, summary: band.summary }}
            lang={lang}
            onLangChange={setLang}
          />
          {/* **소리의 주인을 적는 한 줄.** 탭을 옮겨도 예약된 소리는 걷히지 않으므로, 이
              탭에 서 있는 동안 우는 소리가 누구 것인지를 화면이 말해야 한다 — 「이름 없는
              카운트다운은 존재하지 않는다」의 소리 쪽 짝이다. **고른 것이 없으면 이 줄도
              없다**: 없는 것을 「없습니다」로 적으면 자리만 차지하고, 있는 것을 못 적는
              것보다 낫지도 않다. */}
          {signal !== null && (
            <p
              data-testid="defense-signal-owner"
              data-roster={signal.rosterName}
              data-tense={signal.tense}
              className="col-narrow pt-group text-sm"
            >
              {SIGNAL_PHRASE[signal.tense](t)
                .replaceAll('{roster}', signal.rosterName)
                .replaceAll('{remaining}', signal.remainingText)}
            </p>
          )}
          {/* **attack 쪽에 고를 rally 가 있는데 아직 아무것도 안 골랐으면 그렇다고 말한다**
              (T2 개정 둘째, 2026-10-01 사용자). CT 가 DEFENSE 탭을 보고 있으면 이 화면이 먼저
              서는데, 입장 카드가 약속한 rally 목록과 signal 은 attack 보기에 있다. 대리 관측이
              자기 rally 가 50초 뒤 열리는 사람이 빈 수비 화면에 서는 것을 잡았다. **고른 뒤에는
              없다.** 그때는 위의 신호 주인 줄이 같은 말을 이미 하고 있다. */}
          {attackRallies > 0 && signal === null && (
            <div
              data-testid="defense-attack-waiting"
              className="col-narrow mt-group"
            >
              <div className="note note-accent flex flex-wrap items-center justify-between gap-2 text-sm">
                <span>{t.attackWaiting.replaceAll('{n}', String(attackRallies))}</span>
                <button
                  type="button"
                  data-testid="defense-attack-waiting-go"
                  className="btn btn-primary"
                  onClick={() => {
                    setPicked('attack')
                  }}
                >
                  {t.attackWaitingGo}
                </button>
              </div>
            </div>
          )}
          {/* **지나간 신호는 「지금 울릴 소리」가 아니다.** 개시가 지난 뒤에도 그렇게
              말하면 화면이 또 한 번 시제를 틀린다 — 그때는 「수비에는 소리 신호가
              없습니다」가 다시 참이다. */}
          <ViewDefenseScreen
            store={store}
            nowMs={clock.utcMillis}
            lang={lang}
            signalLive={signal?.tense === 'upcoming'}
          />
        </>
      )}

      {/* 아래 고정 영역(손잡이 44px 와 탭 바)이 본문 끝을 덮지 않게 자리를 비운다. **attack 탭은 이 빈 칸을 쓰지
          않는다.** 그 화면은 같은 높이(`--dock-h`)의 빈 칸 요소를 자기 `main` 맨 끝에 둔다(여백으로는 머리 밀림을 못 막았다). 바깥 빈 칸이면 낮은 화면에서
          `main` 이 먼저 끝나 그 안에 붙은 신호 존 머리가 함께 밀렸다(T6 개정 셋째 적대 리뷰). */}
      {canSwitch && active === 'defense' && (
        <div
          className="h-28"
          style={dockHeight > 0 ? { height: `${String(dockHeight + 16)}px` } : undefined}
          aria-hidden="true"
        />
      )}

      {/* 손잡이와 탭 바를 아래에 함께 고정한다. 연맹원은 폰으로만 오고, 위에 두면 두 화면의
          sticky 고정줄과 같은 자리를 다투게 된다. 그때 어느 하나가 다른 하나를 덮는다. */}
      {canSwitch && (
        <div ref={dockRef} className="fixed inset-x-0 bottom-0 z-20 shadow-band-up">
          {/* **펼친 명단은 손잡이 위로 자란다.** 손잡이와 탭 바가 엄지 자리에 그대로 남아,
              펼친 사람이 같은 자리를 다시 눌러 접는다. 기본은 접힘이다. 연맹원 화면의 1순위는
              카운트다운이고 명단은 확인할 때만 연다 (2026-09-17 사용자). */}
          {presenceOpen && (
            <div
              id="view-presence-panel"
              data-testid="view-presence-panel"
              className="dock-pad max-h-[50vh] overflow-y-auto border-t border-line-hair bg-surface-sunk pb-group"
            >
              <PresenceList
                entries={live.entries}
                connected={live.connected || !presenceSeen}
                lang={lang}
                withGameId={false}
                flush
              />
            </div>
          )}
          {/* N 은 **사람 수**다. 줄마다 적힌 접속 수를 더하지 않는다 (2026-10-01 사용자). */}
          <button
            type="button"
            data-testid="view-presence-handle"
            aria-expanded={presenceOpen}
            aria-controls="view-presence-panel"
            className="dock-pad flex min-h-[44px] w-full items-center justify-between border-t border-line-hair bg-surface-sunk/95 text-sm font-medium backdrop-blur"
            onClick={() => {
              setPresenceOpen((open) => !open)
            }}
          >
            <span className="flex items-center gap-2">
              <Icon name="users" className="text-ink-mute" />
              {t.presenceHandle.replaceAll('{n}', presenceSeen ? String(live.entries.length) : '…')}
              {presenceSeen && !live.connected && (
                <span data-testid="view-presence-stale" className="ml-2 text-ink-mute">
                  {t.presenceHandleStale}
                </span>
              )}
            </span>
            <span aria-hidden="true" className="text-ink-mute">
              {presenceOpen ? '▾' : '▴'}
            </span>
          </button>
          <nav
            data-testid="view-tabbar"
            aria-label={t.tabWhy}
            className="dock-pad flex gap-1 border-t border-line-hair bg-surface-sunk/95 backdrop-blur"
          >
            {(['attack', 'defense'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                data-testid={`view-tab-${tab}`}
                aria-pressed={active === tab}
                className={`tab tab-up flex-1 uppercase ${active === tab ? 'tab-on' : ''}`}
                onClick={() => {
                  setPicked(tab)
                }}
              >
                <Icon name={tab === 'attack' ? 'attack' : 'defense'} />
                {tab === 'attack' ? t.tabAttack : t.tabDefense}
              </button>
            ))}
          </nav>
        </div>
      )}
    </>
  )
}
